import { ChevronDown, ChevronUp, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { StatusBadge } from "@/components/ui/status-badge";
import { Progress } from "@/components/ui/progress";
import type { LucideIcon } from "lucide-react";
import type { CardStatus } from "@/components/ui/status-badge";

type CardHeaderContentProps = {
  icon: LucideIcon;
  title: string;
  subtitle: string;
  status: CardStatus;
  compact: boolean;
  progress?: number;
  locked: boolean;
  note?: string;
  canExpand: boolean;
  isExpanded: boolean;
  isDisabled: boolean;
};

export default function CardHeaderContent({
  icon: Icon,
  title,
  subtitle,
  status,
  compact,
  progress,
  locked,
  note,
  canExpand,
  isExpanded,
  isDisabled,
}: CardHeaderContentProps) {
  const iconBgClass = isDisabled ? "bg-ds-surface-100" : "bg-ds-blue-50";
  const iconColorClass = isDisabled ? "text-ds-text-400" : "text-ds-blue-600";
  const showProgressBar = progress !== undefined && !isDisabled;

  const progressBarColor =
    progress !== undefined && progress >= 100
      ? "bg-ds-green-500"
      : progress !== undefined && progress >= 50
        ? "bg-yellow-400"
        : "bg-ds-amber-500";

  return (
    <>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={cn(
              "w-10 h-10 rounded-lg flex items-center justify-center shrink-0",
              iconBgClass
            )}
          >
            {locked ? (
              <Lock size={20} className="text-ds-text-400" />
            ) : (
              <Icon size={20} className={iconColorClass} />
            )}
          </div>
          <div>
            <h3 className="text-sm md:text-base font-semibold font-heading text-navy-900 leading-tight">
              {title}
            </h3>
            <p className="text-xs text-ds-text-500">{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <StatusBadge status={status} compact={compact} />
          {canExpand &&
            (isExpanded ? (
              <ChevronUp size={16} className="text-ds-text-400" />
            ) : (
              <ChevronDown size={16} className="text-ds-text-400" />
            ))}
        </div>
      </div>

      {showProgressBar && (
        <div className="mt-3">
          <div className="flex justify-between text-xs mb-1">
            <span className="text-ds-text-500">Progres</span>
            <span className="text-ds-text-500">{progress}%</span>
          </div>
          <Progress
            value={progress}
            className="h-2 bg-ds-surface-100"
            indicatorClassName={progressBarColor}
          />
        </div>
      )}

      {locked && note && (
        <div className="flex items-center gap-2 mt-3 text-xs text-ds-text-500">
          <Lock size={14} />
          <span>{note}</span>
        </div>
      )}
    </>
  );
}
